import { Group, Menu, Paper, Stack, Table, Text } from "@mantine/core"
import { useRouter } from "next/navigation"
import { type PropsWithChildren, use } from "react"
import { useTranslation } from "react-i18next"
import { type useAppPaginationQuery, useEnsureUsers, useQueryState, useUsers } from "@/hooks"
import type { IMetaTeamFeedback, TSession, TSessionStats } from "@/types"
import { buildSessionURL, dayjs, SessionContext } from "@/utils"
import { SortHeader } from "../controls"
import { UsersAvatarGroup } from "../people"
import { PEERS_METHODS } from "../surveys/constants"
import { AppPagination, AppTable, icons, ManagerOnly, NoData, TableSkeleton, TeamTag } from "../utils"
import { SessionMenu } from "./SessionMenu"
import { SessionPopulationShort } from "./SessionPopulation"
import { SessionRecurrence } from "./SessionRecurrence"
import { SessionState } from "./SessionState"

interface IProps extends PropsWithChildren {
  isLoading?: boolean
  head?: React.ReactNode
  limit?: number
  data?: TSession[]
  meta?: Record<string, TSessionStats>
  fallback?: () => React.ReactNode
  paginate: ReturnType<typeof useAppPaginationQuery>
}

export function SessionsTable({ isLoading, head, children, fallback, paginate, meta = {}, data = [] }: IProps) {
  const { t } = useTranslation()
  const hasRecurrence = data?.some((ses) => ses.rrule)
  const hasPeers = data?.some((ses) => ses.type === "feedback")

  if (!isLoading && !data?.length)
    return (
      <Paper my="xl" p="xl">
        {fallback ? fallback() : <NoData />}
      </Paper>
    )

  return (
    <Stack mb="lg">
      <AppTable minWidth={500} withRowBorders={!isLoading}>
        {head ?? (
          <Table.Thead>
            <Table.Tr>
              <Table.Th miw={300}>
                <SortHeader field="title">{t("Title")}</SortHeader>
              </Table.Th>
              <Table.Th>
                <SortHeader field="dtstart" defaultActive>
                  {t("Start time")}
                </SortHeader>
              </Table.Th>
              <ManagerOnly>
                <Table.Th>
                  <SortHeader field="visibility" justify="flex-end">
                    {t("Visibility")}
                  </SortHeader>
                </Table.Th>
              </ManagerOnly>
              {hasRecurrence && <Table.Th>{t("Recurrency")}</Table.Th>}
              <Table.Th ta="right">{t("Part")}</Table.Th>
              {hasPeers && <Table.Th ta="right">{t("Peers")}</Table.Th>}
              <Table.Th>
                {
                  <SortHeader field="state" justify="flex-end">
                    {t("State")}
                  </SortHeader>
                }
              </Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
        )}
        <Table.Tbody>
          {isLoading ? (
            <TableSkeleton limit={paginate.limit} colSpan={5 + (hasRecurrence ? 1 : 0) + (hasPeers ? 1 : 0)} />
          ) : (
            data.map((ses) => (
              <SessionContext.Provider value={ses} key={ses.id}>
                {children ?? <SessionRow key={ses.id} meta={meta?.[ses.id]} rrule={hasRecurrence} peers={hasPeers} />}
              </SessionContext.Provider>
            ))
          )}
        </Table.Tbody>
      </AppTable>
      <AppPagination {...paginate} />
    </Stack>
  )
}

function SessionRow({ meta, rrule, peers }: { meta?: TSessionStats; rrule?: boolean; peers?: boolean }) {
  const { t } = useTranslation()
  const session = use(SessionContext)!
  const url = buildSessionURL(session, session.state == "finished" ? "summary" : undefined)
  const [value, setValue] = useQueryState("activity_id")
  const { push } = useRouter()

  return (
    <Table.Tr
      onClick={() => push(url)}
      style={{ cursor: "pointer" }}
      c={session.dtstart > Date.now() ? "dimmed" : undefined}
    >
      {/* Title */}
      <Table.Td>{session.title}</Table.Td>

      {/* Start time */}
      <Table.Td style={{ whiteSpace: "nowrap" }} title={dayjs(session.dtstart).format("LLLL")}>
        <DateRange session={session} />
      </Table.Td>

      {/* Visibility */}
      <ManagerOnly>
        <Table.Td tt="capitalize" ta="right">
          {t(session.visibility)}
        </Table.Td>
      </ManagerOnly>

      {/* Recurrency */}
      {rrule && (
        <Table.Td>
          <SessionRecurrence rrule={session.rrule} />
        </Table.Td>
      )}

      {/* Participation */}
      <Table.Td ta="right">
        {session.state == "confirmed" ? (
          <SessionPopulationShort population={session.population} meta={session.meta} />
        ) : session.state != "canceled" && meta ? (
          <Group justify="flex-end" gap={4} wrap="nowrap">
            <Text fw={500}>
              {meta.finished}/{meta.total}
            </Text>
            <icons.User size={16} />
          </Group>
        ) : (
          <Text c="dimmed">—</Text>
        )}
      </Table.Td>

      {/* Peers */}
      {peers && (
        <Table.Td ta="right">
          <SessionPeersPreview meta={session.meta} />
        </Table.Td>
      )}

      {/* State */}
      <Table.Td ta="right">
        <SessionState state={session.state} />
      </Table.Td>

      {/* Actions */}
      <Table.Td align="right">
        <ManagerOnly>
          <SessionMenu session={session}>
            <Menu.Item
              leftSection={<icons.Filter size={14} />}
              disabled={value == session.activity_id}
              onClick={() => setValue(session.activity_id)}
            >
              {t("Filter by sessions")}
            </Menu.Item>
          </SessionMenu>
        </ManagerOnly>
      </Table.Td>
    </Table.Tr>
  )
}

function SessionPeersPreview({ meta }: Pick<TSession, "meta">) {
  const { t } = useTranslation()
  const { peers = "all", peer_ids = [], peer_team_ids = [] } = meta as IMetaTeamFeedback
  const manual = peers === "manual"
  useEnsureUsers(manual ? peer_ids : [])
  const { findUsers } = useUsers()

  return (
    <Group justify="flex-end" gap={4}>
      {manual ? (
        <>
          {peer_ids.length > 0 && <UsersAvatarGroup users={findUsers(peer_ids)} limit={3} size={32} withTooltip />}
          {peer_team_ids.map((teamId) => (
            <TeamTag key={teamId} id={teamId} />
          ))}
        </>
      ) : (
        <Text span>{t(PEERS_METHODS.find((method) => method.value === peers)?.label || peers)}</Text>
      )}
    </Group>
  )
}

function DateRange({ session }: { session: TSession }) {
  const dtstart = dayjs(session.dtstart)
  const dtfinish = dayjs(session.dtfinish)
  const sameDay = dtstart.isSame(dtfinish, "day")
  const sameMonth = dtstart.isSame(dtfinish, "month")
  return (
    <>
      {dtstart.format("HH:mm / ")}
      {sameDay
        ? dtstart.format("D MMM")
        : sameMonth
          ? `${dtstart.format("D")}-${dtfinish.format("D MMM")}`
          : `${dtstart.format("D MMM")} - ${dtfinish.format("D MMM")}`}
    </>
  )
}
